import { z } from "zod";
import {
	prepareX5MachineData,
	type MachineCorrugateTrackWithTolerance,
	type X5Machine,
	type X5MachineRequest,
	x5MachineSchema,
} from "@packsize/schemas/machines";

import { packNetAxios } from "@/lib/api-client";

import { baseRoutes } from "../base-routes";

const x5Api = `${baseRoutes.x5}/api/v1/machines`;

export async function createX5Machine(data: X5MachineRequest): Promise<void> {
	await packNetAxios.post(x5Api, data);
}

export async function updateX5Machine(tenantId: string, machineId: string, data: X5MachineRequest): Promise<void> {
	// TODO @eb: Remove machineId and tenantId from the payload when the backend no longer requires it.
	await packNetAxios.put(`${x5Api}/${machineId}/configuration`, {
		id: machineId,
		tenantId,
		...data,
	});
}

export async function getX5Machines(): Promise<X5Machine[]> {
	const response = await packNetAxios.get(x5Api);

	if (Array.isArray(response.data) === false) {
		throw new Error("Unable to fetch X5 machines, response is not a list");
	}

	return z
		.array(x5MachineSchema)
		.enrich({ callSite: "getX5Machines - v2" })
		.parseWithTelemetry(response.data.map(prepareX5MachineData));
}

export async function getX5Machine(machineId: string): Promise<X5Machine | null> {
	const response = await packNetAxios.get(`${x5Api}/${machineId}`);

	if (response.status !== 200) {
		return null;
	}

	return x5MachineSchema
		.enrich({ callSite: "getX5Machine - v2" })
		.parseWithTelemetry(prepareX5MachineData(response.data));
}

export async function updateX5Tracks(
	machineId: string,
	tenantId: string,
	tracks: MachineCorrugateTrackWithTolerance[],
): Promise<void> {
	await packNetAxios.put(`${baseRoutes.x5}/api/v1.0/machines/${machineId}/corrugates`, {
		MachineId: machineId,
		TenantId: tenantId,
		Tracks: tracks.map((track) => ({
			corrugateId: track.assignedMaterial.id,
			trackNumber: track.number,
			corrugateWidth: track.assignedMaterial.width,
			corrugateThickness: track.assignedMaterial.thickness,
			corrugateQuality: 0,
			corrugatePrint: track.assignedMaterial.quality,
			alias: track.assignedMaterial.alias,
			isTrackEmpty: track.isEmpty,
			loadedCorrugate: track.assignedMaterial,
		})),
	});
}
